import './AdminPage.css';

import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { ErrorCatalogueEntry } from '../api/errors';
import DataTable from '../components/DataTable';
import { useAllErrorCatalogues, useDeleteErrorCode, useErrorCatalogues } from '../query/errors';

/** One table row: an error page, plus which catalogue it belongs to. */
interface Row extends ErrorCatalogueEntry {
  catalogueId: string;
  catalogueName: string;
  /** URL segment, which is what the delete route addresses. */
  segment: string;
}

const muted = { color: 'var(--text-muted)', fontSize: '0.8rem' } as const;

/**
 * The error registry, for cleanup.
 *
 * Ingest never deletes: a code stays reachable after the owning service stops
 * publishing it, because its `type` URI has already shipped inside responses
 * partners hold. That is right for a withdrawn code and wrong for a mistake —
 * a mistyped path, a code uploaded from the wrong branch — which would
 * otherwise be served forever. This page is the way out.
 *
 * Deleting a code a service still publishes only removes it until that service
 * uploads again, so those rows ask for a second confirmation and say so. The
 * fix for a live code is in the producer's artifact, not here.
 */
export default function AdminErrorsPage() {
  const cataloguesQuery = useErrorCatalogues();
  const catalogueIds = (cataloguesQuery.data ?? []).map((c) => c.catalogueId);
  const catalogues = useAllErrorCatalogues(catalogueIds);
  const deleteMutation = useDeleteErrorCode();

  // The path awaiting a "delete anyway" confirmation, if any.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const rows: Row[] = catalogues.flatMap((query) => {
    const doc = query.data;
    if (!doc) {
      return [];
    }
    return doc.codes.map((entry) => ({
      ...entry,
      catalogueId: doc.catalogueId,
      catalogueName: doc.displayName,
      segment: entry.path.slice(entry.path.lastIndexOf('/') + 1),
    }));
  });

  const loading = cataloguesQuery.isPending || catalogues.some((q) => q.isPending);

  const remove = (row: Row, force: boolean) => {
    setMessage(null);
    deleteMutation.mutate(
      { catalogueId: row.catalogueId, code: row.segment, force },
      {
        onSuccess: () => {
          setConfirming(null);
          setMessage({ text: `Deleted ${row.path}`, ok: true });
        },
        onError: (err) => {
          // 409 is the guard, not a failure: the code is still published, so
          // say what will happen and let the admin insist.
          if (err instanceof ApiError && err.status === 409) {
            setConfirming(row.path);
            setMessage({ text: err.message, ok: false });
            return;
          }
          setConfirming(null);
          setMessage({ text: String(err), ok: false });
        },
      },
    );
  };

  const columns: ColumnDef<Row, unknown>[] = [
    {
      id: 'catalogue',
      header: 'Catalogue',
      accessorFn: (row) => row.catalogueName,
      cell: ({ row }) => (
        <Link to={`/errors/${row.original.catalogueId}`}>{row.original.catalogueName}</Link>
      ),
    },
    {
      id: 'code',
      header: 'Code',
      accessorFn: (row) => row.code,
      cell: ({ row }) => (
        <Link style={{ fontFamily: 'var(--font-mono)' }} to={row.original.path}>
          {row.original.code}
        </Link>
      ),
    },
    {
      id: 'path',
      header: 'Type URI',
      accessorFn: (row) => row.path,
      cell: ({ row }) => <code style={{ fontSize: '0.75rem' }}>{row.original.path}</code>,
    },
    {
      id: 'scope',
      header: 'Scope',
      accessorFn: (row) => row.scope,
    },
    {
      id: 'publishers',
      header: 'Published by',
      enableSorting: false,
      cell: ({ row }) => {
        const { publishedBy, retiredAt } = row.original;
        if (publishedBy.length) {
          return <span style={{ fontSize: '0.8rem' }}>{publishedBy.join(', ')}</span>;
        }
        return <span style={muted}>retired{retiredAt ? ` ${retiredAt.slice(0, 10)}` : ''}</span>;
      },
    },
    {
      id: 'firstSeenAt',
      header: 'First seen',
      accessorFn: (row) => row.firstSeenAt,
      cell: ({ row }) => <span style={muted}>{row.original.firstSeenAt.slice(0, 10)}</span>,
    },
    {
      id: 'lastSeenAt',
      header: 'Last seen',
      accessorFn: (row) => row.lastSeenAt,
      cell: ({ row }) => <span style={muted}>{row.original.lastSeenAt.slice(0, 10)}</span>,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const entry = row.original;
        const busy =
          deleteMutation.isPending &&
          deleteMutation.variables?.catalogueId === entry.catalogueId &&
          deleteMutation.variables?.code === entry.segment;

        if (confirming === entry.path) {
          return (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn-primary btn-sm"
                disabled={busy}
                onClick={() => remove(entry, true)}
              >
                {busy ? 'Deleting…' : 'Delete anyway'}
              </button>
              <button className="btn-primary btn-sm" onClick={() => setConfirming(null)}>
                Cancel
              </button>
            </div>
          );
        }

        return (
          <button
            className="btn-primary btn-sm"
            disabled={busy}
            onClick={() => remove(entry, false)}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        );
      },
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Error registry</h1>
        <p>
          Every error page on record. Pages accumulate on ingest and are never removed automatically
          — a <code>type</code> URI already in the wild has to keep resolving.
        </p>
        <nav style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Link className="btn-primary btn-sm" to="/admin">
            Releases
          </Link>
          <Link className="btn-primary btn-sm" to="/errors">
            Public pages
          </Link>
        </nav>
      </div>

      <section className="releases-section">
        <h2 className="releases-heading">
          Registered codes
          {rows.length > 0 && (
            <span style={{ ...muted, marginLeft: 8, fontWeight: 400 }}>
              {rows.filter((r) => !r.retiredAt).length} current ·{' '}
              {rows.filter((r) => r.retiredAt).length} retired
            </span>
          )}
        </h2>

        {message && (
          <p
            className={`publish-message ${
              message.ok ? 'publish-message--ok' : 'publish-message--err'
            }`}
            style={{ margin: '0 0 12px' }}
          >
            {message.text}
          </p>
        )}

        {loading && <p className="releases-loading">Loading error registry…</p>}
        {cataloguesQuery.isError && (
          <p className="upload-message error">Could not load the error registry.</p>
        )}

        {!loading && !cataloguesQuery.isError && (
          <DataTable
            className="releases-table"
            columns={columns}
            data={rows}
            emptyMessage="No error pages registered. A service registers one by uploading its catalogue."
            enableSorting={true}
            getRowId={(row) => row.path}
            initialSorting={[{ id: 'catalogue', desc: false }]}
          />
        )}
      </section>
    </div>
  );
}
